import { QLD_SITE, SITE_TITLE } from "../misc";
import React, { useCallback, useEffect, useState, useRef } from "react";
import ReactResizeDetector from "react-resize-detector";
// import { Link } from "react-router-dom";
import { QLD } from "../../Components/SVG";
import Modal from "react-bootstrap/Modal";

import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useForm } from "react-hook-form";
import { feedbackFormSubmit, FeedbackFormGeneralError } from "../../api";
import { StdButton } from "../StdButton";

import { event as feedbackFormSubmitEvent } from "../GA/FeedbackForm/FormSubmit";
import { event as logoClickEvent } from "../GA/Home/QueenslandLogoButton";
import { useLocation } from "react-router-dom";
import styles from "./PageHeader.module.scss";
import { LoadingIndicator } from "../../Components/LoadingIndicator";

const LEAVE_FEEDBACK_BUTTON = "leave-feedback-button";
const siteSatisfaction = [
    {
        label: "Very dissatisfied",
        value: "1",
        score: 1
    },
    {
        label: "Dissatisfied",
        value: "2",
        score: 2
    },
    {
        label: "Neither satisfied or dissatisfied",
        value: "3",
        score: 3
    },
    {
        label: "Satisfied",
        value: "4",
        score: 4
    },
    {
        label: "Very satisfied",
        value: "5",
        score: 5
    }
];

type FormValues = {
    comment: string;
    experience: string;
};

const formDefaultValue = {
    comment: undefined,
    experience: undefined
};

const formSubmitStatus = {
    error: undefined,
    loading: false
};

export interface FeedBackModalProps {
    show: {
        show: boolean;
    };
    onHide: () => void;
    fullscreen?: boolean;
}

export const FeedBackModal: React.FC<FeedBackModalProps> = ({ show, onHide, fullscreen = false }) => {
    const { pathname } = useLocation();

    const {
        register,
        handleSubmit,
        watch,
        reset,
        formState: { errors }
    } = useForm<FormValues>({
        defaultValues: formDefaultValue
    });

    const [formSubmitted, setFormSubmitted] = useState<{
        error: boolean | string;
        loading: boolean;
    }>(formSubmitStatus);

    useEffect(() => {
        if (show.show === true) {
            setFormSubmitted(formSubmitStatus);

            reset(formDefaultValue);
        }
    }, [show, reset]);

    const { executeRecaptcha } = useGoogleReCaptcha();
    const token = useRef<string>(undefined);
    const handleReCaptchaVerify = useCallback(async () => {
        if (!executeRecaptcha) {
            console.log("Execute recaptcha not yet available");
            return;
        }

        const _token = await executeRecaptcha("homepage");

        token.current = _token;
        return _token;
    }, [executeRecaptcha]);

    // You can use useEffect to trigger the verification as soon as the component being loaded
    useEffect(() => {
        handleReCaptchaVerify();
    }, [handleReCaptchaVerify]);

    const onSubmit = useCallback(
        async (data: FormValues) => {
            if (token.current == null) {
                token.current = await handleReCaptchaVerify();
            }

            const { label: experienceWithTheSiteLabel, score: experienceWithTheSiteScore } =
                siteSatisfaction?.find(d => d.value === data?.experience) ?? {};

            try {
                feedbackFormSubmitEvent(pathname, {
                    formComment: data?.comment,
                    formExperienceLabel: experienceWithTheSiteLabel,
                    formExperienceScore: experienceWithTheSiteScore
                });

                setFormSubmitted({
                    error: undefined,
                    loading: true
                });

                const res = await feedbackFormSubmit({
                    experienceWithTheSiteScore,
                    experienceWithTheSiteLabel,
                    comment: data.comment,
                    captchaToken: token.current
                });

                if (!res) throw new Error("Server might be busy to process the request");

                if (!res?.success) {
                    let errorMessage = "";
                    if (res?.error === "validation") errorMessage = "Validation error occurs";
                    else if (res.error === "captcha" || res.error === "sender")
                        errorMessage = (res?.data as FeedbackFormGeneralError)?.message;

                    throw new Error(errorMessage);
                }

                setFormSubmitted({
                    error: false,
                    loading: false
                });
            } catch (err) {
                console.log(err);
                setFormSubmitted({
                    error: err?.message ?? true,
                    loading: false
                });
            }

            await handleReCaptchaVerify();
        },
        [handleReCaptchaVerify, pathname]
    );

    const updateMargin = useCallback(() => {
        const findFeedbackButton = document.querySelector(`#${LEAVE_FEEDBACK_BUTTON}`) as HTMLElement;

        if (!findFeedbackButton) return;

        const buttonY = findFeedbackButton?.getBoundingClientRect()?.y;
        const buttonHeight = findFeedbackButton?.getBoundingClientRect()?.height;

        if (buttonY == null || buttonHeight == null) return;

        const modalMarginTop = fullscreen === true ? 0 : buttonY + buttonHeight + 8;
        // const modalMarginTop = buttonY + buttonHeight + 8;

        const dialog = document.querySelector(`.${styles.feedbackModal} .modal-dialog`) as HTMLElement;
        if (dialog) {
            // dialog.style.marginTop = "20px important";
            dialog.style.setProperty("margin-top", `${modalMarginTop}px`);
        }
    }, [fullscreen]);

    useEffect(() => {
        updateMargin();
    }, [updateMargin]);

    const onEnter = useCallback(() => {
        updateMargin();
    }, [updateMargin]);

    const experience = watch("experience");
    const comment = watch("comment");

    return (
        <Modal
            show={show?.show}
            onHide={onHide}
            backdrop={formSubmitted?.loading === true ? "static" : undefined}
            // fullscreen={true}
            // fullscreen={true}
            // ${fullscreen === true ? `${styles.fullscreen}` : ""}
            className={`${styles.feedbackModal}`}
            onEnter={onEnter}
            // style={{
            //     marginTop: modalMarginTop
            // }}
        >
            {formSubmitted?.error == null && (
                <Modal.Header>
                    <Modal.Title>Leave your feedback</Modal.Title>
                </Modal.Header>
            )}
            <Modal.Body className={`${formSubmitted?.error != null ? `${styles.bodyWithThankyou}` : ""}`}>
                {formSubmitted?.error == null && (
                    <>
                        <p className={styles.section}>
                            How satisfied are you with your experience using this website?
                            <span className={styles.requiredField}> *</span>
                        </p>
                        <div className={styles.margin}></div>

                        <ul className={styles.optionList}>
                            {siteSatisfaction.map(d => {
                                return (
                                    <li key={`experience-${d.value}`}>
                                        <input
                                            {...register("experience", {
                                                required: "Please rate the experience you have."
                                                // onChange: event => {
                                                //     console.log(event);
                                                //     console.log(event?.target?.value);
                                                // }
                                            })}
                                            id={`${d.value}`}
                                            type="radio"
                                            checked={d.value === experience}
                                            value={d.value}
                                        />
                                        <label htmlFor={`${d.value}`}>
                                            {d.label} ({d.value})
                                        </label>
                                    </li>
                                );
                            })}

                            {errors.experience?.type != null && (
                                <li className={styles.alert}>{errors.experience.message}</li>
                            )}
                        </ul>
                        <div className={styles.margin}></div>

                        <p className={styles.section}>
                            Comments <span className={styles.requiredField}> *</span>
                        </p>
                        <div className={styles.margin}></div>
                        <textarea
                            {...register("comment", { required: "Please leave a comment." })}
                            value={comment ?? ""}
                            rows={4}
                            className={styles.commentTextArea}
                        ></textarea>
                        {errors.comment?.type != null && <p className={styles.alert}>{errors.comment.message}</p>}
                        <div className={styles.margin}></div>

                        <StdButton
                            onClick={handleSubmit(onSubmit)}
                            disabled={formSubmitted?.loading === true}
                            className={styles.submitButton}
                            variant="std"
                        >
                            {formSubmitted?.loading === false ? (
                                <div className={styles.enabled}>Submit feedback</div>
                            ) : (
                                <LoadingIndicator className={styles.loading} />
                            )}
                        </StdButton>
                        <div className={styles.margin}></div>

                        <p className={styles.copyright}>
                            This site is protected by reCAPTCHA and the Google
                            <a href="https://policies.google.com/privacy" target={"_blank"} rel="noopener noreferrer">
                                Privacy Policy
                            </a>
                            and
                            <a href="https://policies.google.com/terms" target={"_blank"} rel="noopener noreferrer">
                                Terms of Service
                            </a>
                            apply.
                        </p>
                        <div className={styles.margin}></div>
                    </>
                )}

                {formSubmitted?.error != null && (
                    <>
                        <p className={styles.section}>Thank you for your feedback!</p>
                        <div className={styles.margin}></div>
                        {(formSubmitted?.error === false ||
                            (typeof formSubmitted?.error === "string" && formSubmitted?.error?.trim() === "")) && (
                            <>
                                {" "}
                                <p className={styles.feedbackContent}>
                                    Thank you for your feedback. Your feedkback is important to us and will be used to
                                    improve the website.
                                </p>
                                <div className={styles.margin}></div>
                            </>
                        )}

                        {(formSubmitted?.error === true ||
                            (typeof formSubmitted?.error === "string" && formSubmitted?.error?.trim() !== "")) && (
                            <>
                                <p className={styles.feedbackContent}>
                                    There are some issues in receiving your feedback.{" "}
                                    {typeof formSubmitted?.error === "string"
                                        ? `${formSubmitted?.error}${
                                              (formSubmitted?.error as string)?.[
                                                  (formSubmitted?.error as string).length - 1
                                              ] === "."
                                                  ? ""
                                                  : "."
                                          } `
                                        : " "}
                                    Please try again later.
                                </p>
                                <div className={styles.margin}></div>
                            </>
                        )}
                    </>
                )}

                <button className={`${styles.linkButton} ${styles.close}`} onClick={onHide}>
                    Close
                </button>
            </Modal.Body>
        </Modal>
    );
};
export const PageHeader: React.FC<{}> = () => {
    const { pathname } = useLocation();
    const [showFeedbackForm, setShowFeedbackForm] = useState({
        show: false
    });
    const [showFeedbackFormFullScreen, setShowFeedbackFormFullScreen] = useState(false);

    const onFeedbackClose = () => setShowFeedbackForm({ show: false });
    const onFeedbackShow = () => setShowFeedbackForm({ show: true });

    const onResizeContent = useCallback(() => {
        if (window.innerWidth <= 700) {
            setShowFeedbackFormFullScreen(true);
        } else {
            setShowFeedbackFormFullScreen(false);
        }
    }, []);

    return (
        <>
            <div className={styles.pageHeader}>
                <div className={styles.qldLogo}>
                    <a
                        href={QLD_SITE}
                        target={"_blank"}
                        rel="noopener noreferrer"
                        onClick={() => {
                            logoClickEvent(pathname);
                        }}
                    >
                        <QLD />
                    </a>
                </div>
                <div className={styles.pageTitle}>{SITE_TITLE}</div>
                <div className={styles.sideActions}>
                    <button id={LEAVE_FEEDBACK_BUTTON} className={styles.linkButton} onClick={onFeedbackShow}>
                        Leave your feedback
                    </button>
                    {/* <div className={styles.gap}>|</div>
                <Link to={"/help"}>Help</Link> */}
                </div>

                <ReactResizeDetector handleWidth={true} handleHeight={true} onResize={onResizeContent} />
            </div>

            <FeedBackModal show={showFeedbackForm} onHide={onFeedbackClose} fullscreen={showFeedbackFormFullScreen} />
        </>
    );
};
